import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useOrganization, Invitation } from '../../context/OrganizationContext';
import { formatDistanceToNow } from 'date-fns';
import BulkInviteDialog from './BulkInviteDialog';
import { BulkInviteResult } from '../../services/invitation/invitation.service';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { Role } from '../../types';
import { formatDate } from '../../utils/date-formatter';
import { logError } from '../../utils/error-handler';
import { useRBAC } from '../../context/RBACContext';
import { useAuth } from '../../context/AuthContext';

// Helper function to safely format date
const formatExpirationDate = (expiresAt: string | undefined): string => {
  if (!expiresAt) return 'No expiration';
  
  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Error formatting date';
  }
};

interface OrganizationInvitationsProps {
  organizationId: string;
  userRole?: string; // Add optional userRole prop for backup permission checking
}

interface EditInvitationDialogProps {
  open: boolean;
  invitation: Invitation | null;
  onClose: () => void;
  onSave: (invitationId: string, roleId: string) => Promise<void>;
  roles: Role[];
}

const EditInvitationDialog: React.FC<EditInvitationDialogProps> = ({
  open,
  invitation,
  onClose,
  onSave,
  roles
}) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (invitation) {
      setSelectedRole(invitation.roleId || '');
    }
  }, [invitation]);

  const handleSave = async () => {
    if (!invitation || !selectedRole) return;
    
    setIsLoading(true);
    try {
      await onSave(invitation.id, selectedRole);
      onClose();
    } catch (error) {
      console.error('Error updating invitation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Invitation</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Email: {invitation?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Organization: {invitation?.organizationName}
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Role"
            >
              {(roles || []).map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!selectedRole || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OrganizationInvitations: React.FC<OrganizationInvitationsProps> = ({ organizationId, userRole }) => {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuInvitation, setMenuInvitation] = useState<Invitation | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { cancelInvitation } = useOrganization();

  const { userPermissions, hasPermission, loadUserPermissions } = useRBAC();
  const { user } = useAuth();

  // Check permissions for invitation management using actual defined permissions
  const canViewInvitations = userPermissions.some(perm => 
    (perm.resource === 'member' && perm.action === 'read') ||
    (perm.resource === 'organization' && (perm.action === 'read' || perm.action === 'update' || perm.action === 'manage')) ||
    (perm.resource === 'system' && perm.action === 'manage')
  );

  const canManageInvitations = userPermissions.some(perm => 
    (perm.resource === 'member' && perm.action === 'create') ||
    (perm.resource === 'organization' && (perm.action === 'update' || perm.action === 'manage')) ||
    (perm.resource === 'system' && perm.action === 'manage')
  );

  const canCancelInvitations = userPermissions.some(perm => 
    (perm.resource === 'member' && (perm.action === 'delete' || perm.action === 'create')) ||
    (perm.resource === 'organization' && (perm.action === 'update' || perm.action === 'manage')) ||
    (perm.resource === 'system' && perm.action === 'manage')
  );

  // Backup permission checks using userRole (same logic as OrganizationDetail)
  const isOrgAdmin = userRole === 'OrgAdmin' || userRole === 'Organization Administrator';
  const canViewInvitationsBackup = canViewInvitations || isOrgAdmin;
  const canManageInvitationsBackup = canManageInvitations || isOrgAdmin;
  const canCancelInvitationsBackup = canCancelInvitations || isOrgAdmin;



  const fetchOrganizationInvitations = async () => {
    if (!organizationId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const rawInvitationsResponse = await invitationService.listInvitations(organizationId);
      // Extract data array from response
      const rawInvitationsList = Array.isArray(rawInvitationsResponse) ? rawInvitationsResponse : (rawInvitationsResponse.data || []);

      console.log('Raw invitations response:', rawInvitationsResponse);
      console.log('Raw invitations list:', rawInvitationsList);

      // Transform API response to match our Invitation interface
      // Note: invitationService returns snake_case field names
      const transformedInvitations = rawInvitationsList.map((item: any, index: number) => {
        if (!item.id) {
          console.error('Invitation missing ID at index', index, item);
          throw new Error('Invalid invitation data: missing ID');
        }

        const transformed = {
          id: item.id,
          _id: item.id,
          email: item.invitee_email || 'N/A',
          userId: item.invitee_user_id,
          scope: (item.team_id ? 'team' : 'organization') as 'organization' | 'team',
          contextId: item.organization_id || item.team_id,
          organizationId: item.organization_id,
          organizationName: item.organization_name || '',
          teamName: item.team_name || undefined,
          roleId: item.role_id,
          roleName: item.role_name,
          invitedBy: item.inviter_id,
          status: item.status,
          expiresAt: item.expires_at,
          token: item.token || '',
          createdAt: item.created_at,
          updatedAt: item.updated_at
        };

        console.log('Transformed invitation:', transformed);
        return transformed;
      });
      setInvitations(transformedInvitations);
    } catch (err: any) {
      console.error('Error fetching organization invitations:', err);
      setError(err.message || 'Failed to fetch invitations');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    if (!organizationId) return;
    
    try {
      const rolesList = await roleService.getRoles({ organizationId });
      setRoles(rolesList as any);
    } catch (error) {
      logError('Error fetching roles', error);
    }
  };

  useEffect(() => {
    fetchOrganizationInvitations();
    fetchRoles();
    // Load user permissions for current organization
    if (organizationId) {
      loadUserPermissions({ organizationId });
    }
  }, [organizationId, loadUserPermissions]);

  const getStatusText = (status: string | number) => {
    const statusStr = String(status);
    switch (statusStr) {
      case '0': return 'pending';
      case '1': return 'accepted';
      case '2': return 'declined';
      case '3': return 'expired';
      case '4': return 'canceled';
      case 'pending':
      case 'accepted':
      case 'declined':
      case 'expired':
      case 'canceled':
        return statusStr; // Return as-is if already text
      default: return statusStr;
    }
  };

  const getStatusColor = (status: string | number) => {
    const statusStr = String(status);
    switch (statusStr) {
      case 'pending':
      case '0': return 'warning';
      case 'accepted':
      case '1': return 'success';
      case 'declined':
      case '2': return 'error';
      case 'expired':
      case '3': return 'default';
      case 'canceled':
      case '4': return 'default';
      default: return 'default';
    }
  };

  const columns: GridColDef<Invitation>[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'scope',
      headerName: 'Scope',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        <Chip 
          label={params.value} 
          color={params.value === 'organization' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        params.value || '-'
      ),
    },
    {
      field: 'roleName',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        params.value || 'Not specified'
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<Invitation, string | number>) => {
        const statusText = getStatusText(params.value ?? '');
        return (
          <Chip
            label={statusText}
            color={getStatusColor(params.value ?? '') as any}
            size="small"
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Sent',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => {
        const date = params.value;
        return date ? formatDate(date) : '-';
      },
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => {
        const invitation = params.row;
        // Only show expiration for pending invitations - handle both string and numeric formats
        const statusStr = String(invitation.status);
        const isPending = invitation.status === 'pending' || statusStr === '0' || invitation.status === 0;

        if (!isPending) {
          return '-';
        }

        const date = params.value;
        if (!date) return 'No expiration';
        try {
          return formatDistanceToNow(new Date(date), { addSuffix: true });
        } catch (error) {
          return 'Invalid date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      renderCell: (params: GridRenderCellParams<Invitation>) => {
        const invitation = params.row;

        // Check for pending status - handle both string and numeric formats
        const statusStr = String(invitation.status);
        const isPending = invitation.status === 'pending' || statusStr === '0' || invitation.status === 0;

        if (!isPending || !canCancelInvitationsBackup) {
          return null;
        }

        return (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, invitation)}
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => {
    setMenuAnchor(event.currentTarget);
    setMenuInvitation(invitation);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuInvitation(null);
  };

  const handleEditInvitation = () => {
    setSelectedInvitation(menuInvitation);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelInvitation = async () => {
    if (menuInvitation) {
      try {
        await cancelInvitation(menuInvitation.id);
        // Refresh the organization invitations list after canceling
        await fetchOrganizationInvitations();
      } catch (error) {
        console.error('Error canceling invitation:', error);
      }
    }
    handleMenuClose();
  };

  const handleEditSave = async (invitationId: string, roleId: string) => {
    // Note: This would require a new API endpoint to update invitation role
    // For now, we'll just close the dialog
    console.log('Update invitation', invitationId, 'with role', roleId);
  };

  const handleBulkInviteComplete = async (_results: BulkInviteResult) => {
    await fetchOrganizationInvitations();
  };

  // Don't render if user doesn't have permission to view invitations (use backup check)
  if (!canViewInvitationsBackup) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          You don't have permission to view organization invitations.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Organization Invitations
        </Typography>
        <Box display="flex" gap={1}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchOrganizationInvitations}
            disabled={isLoading}
          >
            Refresh
          </Button>
          {canManageInvitationsBackup && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setInviteDialogOpen(true)}
            >
              Invite Member
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={invitations}
          columns={columns}
          loading={isLoading}
          getRowId={(row: Invitation) => row.id}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          localeText={{
            noRowsLabel: 'No invitations sent yet',
            noResultsOverlayLabel: 'No invitations found',
            MuiTablePagination: {
              labelRowsPerPage: 'Invitations per page',
            },
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.default',
              borderBottom: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditInvitation} disabled>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Role (Coming Soon)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCancelInvitation}>
          <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Invitation</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <BulkInviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onComplete={handleBulkInviteComplete}
        organizationId={organizationId}
      />
      
      <EditInvitationDialog
        open={editDialogOpen}
        invitation={selectedInvitation}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditSave}
        roles={roles}
      />
    </Box>
  );
};

export default OrganizationInvitations;